import { LitElement, html } from 'lit';

import { getConfig } from '../../lib/config/manager.js';
import { PollController } from '../../lib/poll/index.js';
import { Router } from '../../lib/router/index.js';
import { SESSION_EXPIRED_EVENT, session } from '../../lib/session/index.js';
import { api } from '../api/index.js';
import { customerRoutes, navigation, routes } from '../routes.js';
import { currentViewer } from '../viewer.js';
import { brandMark } from './brand-mark.js';
import { formStyles } from '../../styles/shared/forms.js';
import { planeAppStyles } from '../../styles/components/plane-app.js';

/** How often the shell counts what waits for an operator, between page changes. */
const PENDING_INTERVAL_MS = 30000;

/**
 * Application shell: the demo band (demo backend only), the header with the main navigation and
 * the session, the offline and install bands (each shows itself only when it has something to
 * say), and the outlet of the current page. When the session expires, the page is
 * replaced by a sign-in notice: what it showed came from a session that no longer exists.
 *
 * Who signed in picks the pages: an operator gets the console (`routes`), a customer user the
 * customer pages (`customerRoutes`), with the product's name and no navigation. No page renders
 * before the shell knows which.
 */
class PlaneApp extends LitElement {
  static properties = {
    user: { state: true },
    viewerKnown: { state: true },
    expired: { state: true },
  };

  static styles = [
    formStyles,
    planeAppStyles,
  ];

  constructor() {
    super();
    this.config = getConfig();
    this.router = new Router(this, routes, this.config.app?.name ?? 'plane');
    this.user = null;
    this.viewerKnown = false;
    this.expired = session.isExpired();
    this.onExpired = () => {
      this.expired = true;
    };
    // what waits for an operator, counted on the links of the navigation (`pending` in routes.js);
    // loaded again on every page change and on a refresh event too
    this.pending = new PollController(
      this,
      async () => (this.user?.operator ? { enrollmentRequests: (await api.enrollmentRequests()).length } : {}),
      PENDING_INTERVAL_MS,
    );
  }

  connectedCallback() {
    super.connectedCallback();
    document.body.addEventListener(SESSION_EXPIRED_EVENT, this.onExpired);
    currentViewer()
      .then((user) => {
        this.user = user;
        if (!user.operator) {
          this.router.routes = customerRoutes;
          this.router.appTitle = this.customerName;
          this.router.resolve();
        } else {
          this.pending.refresh();
        }
      })
      .catch(() => {
        // a 401 reaches the session module on its own; anything else leaves the header anonymous
      })
      .finally(() => {
        this.viewerKnown = true;
      });
  }

  disconnectedCallback() {
    document.body.removeEventListener(SESSION_EXPIRED_EVENT, this.onExpired);
    super.disconnectedCallback();
  }

  get customerName() {
    return this.config.app?.customerName ?? 'Workspace';
  }

  /** A signed-in user who is not an operator: the customer pages. */
  get asCustomer() {
    return this.user !== null && !this.user.operator;
  }

  updated() {
    if (this.pendingFor !== this.router.path) {
      this.pendingFor = this.router.path;
      if (this.user?.operator) {
        this.pending.refresh();
      }
    }
    // The current page in view in the scrolling row of the narrow layout, once per page change; a
    // no-op where the row fits. The row only: scrollIntoView would scroll the window too.
    const nav = this.renderRoot.querySelector('nav');
    const current = nav?.querySelector('a[aria-current="page"]');
    if (!current || this.navScrolledFor === this.router.path) {
      return;
    }
    this.navScrolledFor = this.router.path;
    const left = current.offsetLeft - nav.offsetLeft;
    const visible = nav.clientWidth - parseFloat(getComputedStyle(nav).paddingRight);
    if (left < nav.scrollLeft || left + current.offsetWidth > nav.scrollLeft + visible) {
      nav.scrollLeft = left - (visible - current.offsetWidth) / 2;
    }
  }

  /** The count of what waits behind a link of the navigation, when there is any. */
  renderPending(item) {
    const count = item.pending ? (this.pending.value?.[item.pending] ?? 0) : 0;
    if (count === 0) {
      return null;
    }
    return html`<span class="pending" title="${count} waiting for approval"><span class="visually-hidden">, </span>${count}<span class="visually-hidden"> waiting</span></span>`;
  }

  isActive(href) {
    const path = this.router.path;
    return href === '/' ? path === '/' : path === href || path.startsWith(`${href}/`);
  }

  renderAccount() {
    const auth = this.config.auth ?? {};
    if (!this.user) {
      return html`<a href=${auth.loginUrl}>Sign in</a>`;
    }
    if (this.asCustomer) {
      return html`
        ${this.user.memberships?.length ? html`<a href="/people" aria-current=${this.isActive('/people') ? 'page' : 'false'}>People</a>` : null}
        <span class="user">${this.user.username}</span>
        ${api.demo ? null : html`<a href=${auth.logoutUrl}>Sign out</a>`}
      `;
    }
    return html`
      <span class="user" title=${this.user.operator ? 'Operator' : `Customer user of ${this.user.customers.join(', ')}`}>
        ${this.user.username}${this.user.operator ? '' : html` · ${this.user.customers.join(', ') || 'no customer'}`}
      </span>
      ${this.user.admin ? html`<a href=${this.config.admin?.consoleUrl}>Admin</a>` : null}
      ${api.demo ? null : html`<a href=${auth.logoutUrl}>Sign out</a>`}
    `;
  }

  /** A 401: the session ended, or there never was one (the user is known only after /api/session). */
  renderExpired() {
    const ended = this.user !== null;
    return html`
      <div class="expired">
        <h1>${ended ? 'Your session has ended' : 'Sign in to plane'}</h1>
        <p>
          ${ended
            ? `Sign in again to see the current state of your ${'workspaces'}.`
            : 'Sign in to see your workspaces and their state.'}
        </p>
        <a class="button primary" href=${this.config.auth?.loginUrl}>Sign in</a>
      </div>
    `;
  }

  render() {
    return html`
      ${api.demo ? html`<plane-demo-bar></plane-demo-bar>` : null}
      <header>
        <div class="header-inner">
          <a class="brand" href="/">
            ${brandMark}
            <span class="wordmark">
              ${this.asCustomer ? this.customerName : html`plane<small>control plane</small>`}
            </span>
          </a>
          <nav aria-label="Main">
            ${this.asCustomer
              ? null
              : navigation.filter((item) => !item.operator || this.user?.operator).map(
                  (item) => html`<a href=${item.href} aria-current=${this.isActive(item.href) ? 'page' : 'false'}>${item.label}${this.renderPending(item)}</a>`,
                )}
          </nav>
          <div class="account">${this.renderAccount()}</div>
        </div>
      </header>
      <plane-connection-bar></plane-connection-bar>
      <plane-install-hint></plane-install-hint>
      <main>${this.expired ? this.renderExpired() : this.viewerKnown ? this.router.outlet() : null}</main>
      <footer>
        <div class="footer-inner">
          <span>
            ${this.asCustomer
              ? `${this.customerName} — your workspaces and their assistants, managed for you.`
              : 'Configuration of managed workspaces. States are what the servers report, never presumed.'}
          </span>
          ${this.asCustomer ? null : html`<span class="colophon">plane · sounded, not presumed</span>`}
        </div>
      </footer>
    `;
  }
}

customElements.define('plane-app', PlaneApp);
